import {React, useContext, Link, useLocation} from 'external/react'
import {clsx, styled, TableCell, CheckCircleIcon} from 'external/material'
import {TeacherContext} from 'teacher/context'
import {createPost} from 'helpers/command'
import { displayPoints } from 'helpers/gradebook'

import Options from './GradeCellOptions'
import GradeInput from 'components/gradebook/GradeInput'

const StyledCell = styled(TableCell)(gradeCellStyles)

export default function GradeCell(props) {

  const {student = {}, column = {}, classname=''} = props
  const {Cell, cssClass} = pickCellHandler(column)
  const context = useContext(TeacherContext)
  const {grades = []} = column
  const [homework = {}] = grades
  const {properties = {}} = (column.type == 'homework' && homework.points || column.external)
    ? ((context.rollbookContextStudentAssignmentProperties[student.id] || {})[column.id] || {properties: {}})
    : column.selectedAnswer || {}
  const {published} = properties
  return (
    <StyledCell align="center" className={clsx("root", cssClass, classname)}>
      <div className={clsx("container", {ignore: column.ignore, 'not-assigned': !column.assigned})}>
        <Options {...{column, student}} />
        <div className="cellContainer">
          <Cell {...{column, student}} />
        </div>
        {published && (<span className="published"><CheckCircleIcon size="small" /></span>)}
      </div>
    </StyledCell>
  )
}

function pickCellHandler({external, type, answered, pending, selectedAnswer, grades = []}) {
  if (external) return {Cell: ExternalCell, cssClass: 'external'}
  if (type == 'homework') return {Cell: HomeworkCell, cssClass: 'homework'}

  // Fall back to single grade when no explicit selectedAnswer exists
  const effectiveAnswer = selectedAnswer ?? (grades.length === 1 ? grades[0] : null)

  if (!answered || !effectiveAnswer) return {Cell: NoGradeCell, cssClass: 'nograde'}
  if (pending) return {Cell: PendingReviewCell, cssClass: 'pending'}
  return {
    Cell: ReviewedCell,
    cssClass: effectiveAnswer.properties?.published ? 'published' : 'reviewed'
  }
}

function HomeworkCell({column}) {
  const {grades = []} = column
  const {updateState, link, query} = useContext(TeacherContext)
  const location = useLocation()
  const [homework = {}] = grades

  const reviewPath= homework.id && '/:languageId/:role/:page/:id'
  const reviewLink = homework.id && link({page: 'homework', id: homework.assignment}, reviewPath)

  function homeworkClickHandler() {
    updateState({homeworkContext: {review: {from: {$set: location.pathname}, query: {$set: query}}}})
  }

  return homework.points
    ? (<Link to={reviewLink} onClick={homeworkClickHandler} className={clsx('fill', 'centered', 'link')}>{homework.points}</Link>)
    : (<span className={clsx('fill', 'centered', 'empty')}>-</span>)
}

function ExternalCell({student, column}) {
  const {external, points, total} = column
  const context = useContext(TeacherContext)
  async function handleGradeChange (value) {

    const [grade] = await createPost('grading', 'updateExternalStudentGrade')({student: student.id, external, value: value || 0})
    if (grade) {
      const index = context.rollbookContextGrades.items.findIndex(g => g.id === grade.id)
      if (index >= 0) {
        context.updateState({rollbookContextGrades: {items: {[index]: {$set: grade}}}})
      } else {
        context.updateState({rollbookContextGrades: {items: {$push: [grade]}}})
        
      }
    }

  }

  return <GradeInput total={total} value={points} onGradeChange={handleGradeChange} />

}


function NoGradeCell({column}) {
  return (<span className={clsx('fill', 'centered', 'empty')}>{column.assigned ? '-' : 'NA'}</span>)
}

function ReviewedCell({column}) {
  const {selectedAnswer, grades = []} = column
  const effectiveAnswer = selectedAnswer ?? grades[0]
  return (<ReviewLink answeredAssessment={effectiveAnswer?.answeredAssessment}>
    {displayPoints(effectiveAnswer || {points: column.points})}
  </ReviewLink>)
}

function PendingReviewCell({column}) {
  const {selectedAnswer, grades = []} = column
  const effectiveAnswer = selectedAnswer ?? grades[0]
  return (<ReviewLink answeredAssessment={effectiveAnswer?.answeredAssessment}>R</ReviewLink>)
}

function ReviewLink({answeredAssessment, children}) {
  const {updateState, link, query} = useContext(TeacherContext)
  const location = useLocation()
  const reviewPath='/:languageId/:role/:page(quiz)/:assessment/:action(review)'
  const reviewLink = link({page: 'quiz', assessment: answeredAssessment, action: 'review'}, reviewPath)
  function reviewClickHandler() {
    updateState({quizEditFrom: {$set: location.pathname}, quizEditFromQuery: {$set: query}})
  }
  return (<Link onClick={reviewClickHandler} to={reviewLink} className={clsx('fill', 'centered', 'link')}>{children}</Link>)
}

function gradeCellStyles(theme) {
  return {
    "&.root": {
      width: 40,
      padding: 0,
      color: '#4f576e',
      fontWeight: 500,
      borderLeft: 'solid 1px #d8dbe0',
      borderRight: 'solid 1px #d8dbe0',
      '&.pending': {
        backgroundColor: '#ffeae9',
        '& .link': {
          color: '#da1617'
        }
      }
    },
    "& .container": {
      position: 'relative',
      top: 0,
      height: 60,
      '&.ignore': {
        background: 'repeating-linear-gradient(110deg, rgb(160 156 156) 5px, rgb(150 136 136 / 69%) 9%)',
        '& .published svg': {
          backgroundColor: '#fff',
          borderRadius: '100%'
        }
      }
    },
    "& .published": {
      color: '#70c44b',
      position: 'absolute',
      top: 43,
      left: 2,
      zIndex: 20,
      '& svg': {
        fontSize: '8pt',

      }
    },
    "& .cellContainer": {
      position: 'absolute',
      zIndex: 10,
      justifyContent: 'center',
      top: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      '& .centered': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      '& .fill': {
        flexGrow: 1
      },
      '& .empty': {
        marginLeft: -5,
      },
      '& .link': {
        fontSize: '11pt',
        marginLeft: -5,
        textDecoration: 'none',
        color: '#555c76',
        '&:hover': {
          fontWeight: 'bold'
        }
      },
    }
  }
}
